import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import {
  DocumentGroup,
  PatientDocument,
  WoundDocumentGroup,
} from '../../../../../core/models/document/document.model';
import { DocumentType } from '../../../../../../enums/document-type.enum';
import { PermissionService } from '../../../../../core/services/user/permission.service';

@Component({
  selector: 'app-document-timeline',
  standalone: true,
  imports: [CommonModule, TranslateModule, DatePipe],
  templateUrl: './document-timeline.component.html',
  styleUrl: './document-timeline.component.scss',
})
export class DocumentTimelineComponent {
  @Input() documentGroups: DocumentGroup[] = [];
  @Input() woundGroups: WoundDocumentGroup[] = [];
  @Input() groupByWound = false;

  @Output() previewRequested = new EventEmitter<PatientDocument>();
  @Output() downloadRequested = new EventEmitter<PatientDocument>();
  @Output() deleteRequested = new EventEmitter<PatientDocument>();

  deleteModalDocumentId: string = '';

  constructor(private permissionService: PermissionService) {}

  getFileIcon(document: any): string {
    switch (document.fileType) {
      case DocumentType.PDF:
        return 'fa-file-pdf';
      case DocumentType.PNG:
      case DocumentType.JPG:
      case DocumentType.JPEG:
        return 'fa-file-image';
      case DocumentType.DOC:
      case DocumentType.DOCX:
        return 'fa-file-word';
      default:
        return 'fa-file';
    }
  }

  isPreviewable(document: any): boolean {
    return [
      DocumentType.PDF,
      DocumentType.PNG,
      DocumentType.JPG,
      DocumentType.JPEG,
      DocumentType.DOC,
      DocumentType.DOCX,
    ].includes(document.fileType);
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) {
      return bytes + ' B';
    }
    if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(1) + ' KB';
    }
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  onPreview(document: PatientDocument): void {
    if (this.isPreviewable(document)) {
      this.previewRequested.emit(document);
    }
  }

  onDownload(document: PatientDocument): void {
    this.downloadRequested.emit(document);
  }

  onDelete(document: PatientDocument): void {
    this.deleteModalDocumentId = document.id;

    // Wait for Angular to render the modal in DOM
    setTimeout(() => {
      const modalElement = window.document.getElementById(
        `deleteDocModal-${this.deleteModalDocumentId}`,
      );
      if (modalElement) {
        const modal = new (window as any).bootstrap.Modal(modalElement);
        modal.show();
      }
    }, 0);
  }

  confirmDelete(): void {
    const allDocuments = this.documentGroups
      .flatMap((g) => g.documents)
      .concat(this.woundGroups.flatMap((g) => g.documents));

    const doc = allDocuments.find((d) => d.id === this.deleteModalDocumentId);

    if (doc) {
      this.deleteRequested.emit(doc);
    }
  }

  canDelete(): boolean {
    return this.permissionService.hasPermission('nurse_claim');
  }

  getStatusTranslationKey(status?: number): string {
    switch (status) {
      case 1:
        return 'patientDetail.woundStatus.waitingDoctorAssignment';
      case 2:
        return 'patientDetail.woundStatus.doctorAssigned';
      case 3:
        return 'patientDetail.woundStatus.treatmentDecisionCreated';
      case 4:
        return 'patientDetail.woundStatus.treatmentCompleted';
      case 0:
      default:
        return 'patientDetail.woundStatus.created';
    }
  }
}
